import { on } from "@ngrx/store";
import { Catalog } from "src/api-service";
import { initialCatalogState, CatalogState } from "./catalog.state";
import { CatalogActions } from "./catalog.actions";
import { catalogsReducer } from "./catalogs/catalogs.reducer";
import { PagesStore } from "../pages.state";

// **************************************************************************
// HELPER: Update catalog state within PagesStore
// **************************************************************************

function withCatalog(state: PagesStore, updater: (catalog: CatalogState) => CatalogState): PagesStore {
    return { ...state, catalog: updater(state.catalog) };
}

/** Helper to merge CatalogResponse into Catalog, preserving existing products if not provided */
function mergeCatalog(existing: Catalog | null, response: { id: number; name?: string; imageUrl?: string; status: number; lockCustomerPrices: boolean; includeCustomerPriceDecimals: boolean; currencyCode?: string; subscriptionConceptId?: number; products?: unknown[] }): Catalog {
    return {
        id: response.id,
        name: response.name,
        imageUrl: response.imageUrl,
        status: response.status,
        typeId: existing?.typeId ?? 1,
        subscriptionConceptId: response.subscriptionConceptId,
        lockCustomerPrices: response.lockCustomerPrices,
        includeCustomerPriceDecimals: response.includeCustomerPriceDecimals,
        currencyCode: response.currencyCode,
        countryCode: existing?.countryCode,
        deleted: existing?.deleted,
        created: existing?.created ?? new Date(),
        products: (response.products as Catalog["products"]) ?? existing?.products ?? [],
        linkedContracts: existing?.linkedContracts,
    };
}

// **************************************************************************
// CATALOG REDUCER (array format for PagesStore)
// **************************************************************************

export const catalogReducer = [
    // Spread nested list reducer
    ...catalogsReducer,

    // **************************************************************************
    // LOAD CATALOG
    // **************************************************************************
    on(CatalogActions.loadCatalog, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalog: null,
        error: null,
    }))),

    on(CatalogActions.loadCatalogSuccess, (state: PagesStore, { catalog }) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalog: mergeCatalog(catalogState.catalog, catalog),
        selectedFinancingCompanyId: catalog.financingCompanyId ?? null,
        financingCompanyName: catalog.financingCompanyName ?? null,
        error: null,
    }))),

    on(CatalogActions.loadCatalogFailure, (state: PagesStore, { error }) => withCatalog(state, catalogState => ({
        ...catalogState,
        error,
    }))),

    // **************************************************************************
    // UPDATE CATALOG SETTINGS
    // **************************************************************************
    on(CatalogActions.updateCatalogSuccess, (state: PagesStore, { catalog }) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalog: mergeCatalog(catalogState.catalog, catalog),
        error: null,
    }))),

    on(CatalogActions.updateCatalogFailure, (state: PagesStore, { error }) => withCatalog(state, catalogState => ({
        ...catalogState,
        error,
    }))),

    // **************************************************************************
    // TOGGLE LOCK CUSTOMER PRICES
    // **************************************************************************
    on(CatalogActions.toggleLockCustomerPricesSuccess, (state: PagesStore, { catalog }) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalog: mergeCatalog(catalogState.catalog, catalog),
        error: null,
    }))),

    on(CatalogActions.toggleLockCustomerPricesFailure, (state: PagesStore, { error }) => withCatalog(state, catalogState => ({
        ...catalogState,
        error,
    }))),

    // **************************************************************************
    // TOGGLE INCLUDE CUSTOMER PRICE DECIMALS
    // **************************************************************************
    on(CatalogActions.toggleIncludeCustomerPriceDecimalsSuccess, (state: PagesStore, { catalog }) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalog: mergeCatalog(catalogState.catalog, catalog),
        error: null,
    }))),

    on(CatalogActions.toggleIncludeCustomerPriceDecimalsFailure, (state: PagesStore, { error }) => withCatalog(state, catalogState => ({
        ...catalogState,
        error,
    }))),

    // **************************************************************************
    // PRODUCT OPERATION SUCCESS (unified handler)
    // **************************************************************************
    on(
        CatalogActions.createEntitySuccess,
        CatalogActions.updateEntityFieldSuccess,
        CatalogActions.deleteEntitySuccess,
        CatalogActions.uploadEntityImageSuccess,
        CatalogActions.duplicateEntitySuccess,
        CatalogActions.pasteEntitySuccess,
        (state: PagesStore, { response }) => withCatalog(state, catalogState => ({
            ...catalogState,
            catalog: catalogState.catalog ? {
                ...catalogState.catalog,
                products: response.products ?? [],
            } : null,
            latestOperation: response.latestOperation,
            error: null,
        })),
    ),

    // **************************************************************************
    // BULK PUBLISH SUCCESS
    // **************************************************************************
    on(CatalogActions.bulkPublishProductsSuccess, (state: PagesStore, { response }) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalog: catalogState.catalog ? {
            ...catalogState.catalog,
            products: response.products ?? [],
        } : null,
        error: null,
    }))),

    // **************************************************************************
    // PRODUCT OPERATION FAILURE (unified handler)
    // **************************************************************************
    on(
        CatalogActions.createEntityFailure,
        CatalogActions.updateEntityFieldFailure,
        CatalogActions.deleteEntityFailure,
        CatalogActions.uploadEntityImageFailure,
        CatalogActions.duplicateEntityFailure,
        CatalogActions.pasteEntityFailure,
        (state: PagesStore, { response }) => withCatalog(state, catalogState => ({
            ...catalogState,
            latestOperation: response.latestOperation,
        })),
    ),

    // **************************************************************************
    // RESET
    // **************************************************************************
    on(CatalogActions.resetState, (state: PagesStore) => withCatalog(state, () => ({
        ...initialCatalogState,
    }))),

    // **************************************************************************
    // UI SETTINGS
    // **************************************************************************
    on(CatalogActions.setLayout, (state: PagesStore, { layout }) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalogLayout: layout,
    }))),

    on(CatalogActions.setDensity, (state: PagesStore, { density }) => withCatalog(state, catalogState => ({
        ...catalogState,
        catalogDensity: density,
    }))),

    // **************************************************************************
    // LOADING HANDLERS
    // **************************************************************************

    // Load catalog loading
    on(CatalogActions.loadCatalog, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        loadingDetail: true,
    }))),

    on(CatalogActions.loadCatalogSuccess, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        loadingList: false,
        loadingDetail: false,
        updatingDetail: false,
        loadingBuilder: false,
        lockingPrices: false,
    }))),

    on(CatalogActions.loadCatalogFailure, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        loadingDetail: false,
    }))),

    // Update catalog loading
    on(CatalogActions.updateCatalog, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        updatingDetail: true,
    }))),

    on(CatalogActions.updateCatalogSuccess, CatalogActions.updateCatalogFailure, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        updatingDetail: false,
    }))),

    // Lock customer prices loading
    on(CatalogActions.toggleLockCustomerPrices, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        lockingPrices: true,
    }))),

    on(CatalogActions.toggleLockCustomerPricesSuccess, CatalogActions.toggleLockCustomerPricesFailure, (state: PagesStore) => withCatalog(state, catalogState => ({
        ...catalogState,
        lockingPrices: false,
    }))),
];
